import React, { useEffect, useState } from "react";
import PageHelmet from "../components/PageHelmet";
import {
    Form,
    Button,
    OverlayTrigger,
    Tooltip,
    Alert,
    Modal,
} from "react-bootstrap";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
    FiFileText,
    FiUsers,
    FiLayout,
    FiSend
} from "react-icons/fi";
import "../styles/Plans.css";
import CustomDataGrid from "../components/CustomDataGrid.jsx";
import { MdArrowBackIos } from "react-icons/md";
import {
    generateGridRows,
    generateGridColumns,
    existingPlansGridColumns,
    plansGridOptionalColumns,
    existingPlansRowMapping,
    formatGridValue,
} from "../utils/PageUtils";
import { fetchSubscriptionPlans, createSubscriptionPlan } from "../utils/api";

function Plans({ userRole, bearerToken }) {
    const [planFields, setPlanFields] = useState({
        name: "",
        description: "",
        monthly_price: "",
        annual_price: "",
        monthly_credits: "",
        credit_overage_price: "",
        quotas: {
            Campaigns: "",
            Contacts: "",
            Templates: "",
            Requests: "",
        },
        credit_costs: {
            "Envelope - Download PDF": "",
            "Letter - Download PDF": "",
            "Letter Envelope - Print & Mail": "",
            "Postcard Pro 6x9 - Print & Mail": ""
        }
    });

    const [plans, setPlans] = useState([]);
    const [planError, setPlanError] = useState(null);
    const [createPlanInfo, setCreatePlanInfo] = useState(null);
    const [createPlanError, setCreatePlanError] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [refreshPlans, setRefreshPlans] = useState(true);

    const quotaFields = [
        { name: "Campaigns", label: "Campaigns", icon: <FiLayout /> },
        { name: "Contacts", label: "Contacts", icon: <FiUsers /> },
        { name: "Templates", label: "Templates", icon: <FiFileText /> },
        { name: "Requests", label: "Requests", icon: <FiSend /> },
    ];

    const creditFields = [
        { name: "Envelope - Download PDF", label: "Envelope - Download PDF", icon: <FiFileText /> },
        { name: "Letter - Download PDF", label: "Letter - Download PDF", icon: <FiFileText /> },
        { name: "Letter Envelope - Print & Mail", label: "Letter Envelope - Print & Mail", icon: <FiFileText /> },
        { name: "Postcard Pro 6x9 - Print & Mail", label: "Postcard Pro 6x9 - Print & Mail", icon: <FiFileText /> },
    ]

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const resData = await fetchSubscriptionPlans(bearerToken);
                if (!resData.success) {
                    setPlanError(resData.message || "Unable to fetch Plans");
                    return;
                }
                setPlans(resData?.data?.records || []);
            } catch {
                setPlanError("Unable to fetch Plans");
            }
        };
        if (refreshPlans) {
            fetchPlans();
            setRefreshPlans(false)
        }
    }, [refreshPlans]);

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const handleInputChange = (e) => {
        setCreatePlanError(null);
        setCreatePlanInfo(null);

        const { name, value } = e.target;
        const numericFields = [
            "monthly_price",
            "annual_price",
            "monthly_credits",
            "credit_overage_price",
        ];

        const parsedValue = numericFields.includes(name)
            ? (value === "" ? "" : Number(value))
            : value;

        if (name in planFields.quotas) {
            setPlanFields((prev) => ({
                ...prev,
                quotas: {
                    ...prev.quotas,
                    [name]: value === "" ? "" : Number(value),
                },
            }));
        } else if (name in planFields.credit_costs) {
            setPlanFields((prev) => ({
                ...prev,
                credit_costs: {
                    ...prev.credit_costs,
                    [name]: value === "" ? "" : Number(value),
                },
            }));
        } else {
            setPlanFields((prev) => ({
                ...prev,
                [name]: parsedValue,
            }));
        }
    };

    const resetPlanFields = () => {
        setPlanFields({
            name: "",
            description: "",
            monthly_price: "",
            annual_price: "",
            monthly_credits: "",
            credit_overage_price: "",
            quotas: {
                Campaigns: "",
                Contacts: "",
                Templates: "",
                Requests: "",
            },
            credit_costs: {
                "Envelope - Download PDF": "",
                "Letter - Download PDF": "",
                "Letter Envelope - Print & Mail": "",
                "Postcard Pro 6x9 - Print & Mail": ""
            }
        });
    };

    const handleCreatePlan = async (e) => {
        setCreatePlanError(null);
        setCreatePlanInfo(null);
        e.preventDefault();
        try {
            const res = await createSubscriptionPlan(bearerToken, planFields);
            if (!res.success) {
                setCreatePlanError(res.message || "Unable to create a Plan");
                return;
            }
            setCreatePlanInfo("Plan created successfully!")
            setRefreshPlans(true);
        } catch {
            setCreatePlanError("Something went wrong while creating a plan.");
        }
    };

    const handleOpenCreateModal = () => {
        setShowCreateModal(true);
        resetPlanFields();
    }

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreatePlanError(null);
        setCreatePlanInfo(null);
        resetPlanFields();
    }

    const handleClosePlanDetails = () => {
        setSelectedPlan(null);
        setSelectedRowId(null);
    }

    const handleRowClick = (e) => {
        if (selectedRowId === e.row.id) {
            setSelectedRowId(null);
            setSelectedPlan(null);
            return;
        }
        setSelectedRowId(e.row.id);
        const plan = plans.find((plan) => plan.plan_id === e.row.plan_id);
        setSelectedPlan(plan);
    };

    useEffect(() => {
        // keep body scroll locked when details are open on small screens
        if (selectedPlan && isMobile) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [selectedPlan, isMobile]);


    const existingPlansRows = generateGridRows(plans, existingPlansRowMapping);

    const existingPlansColumns = generateGridColumns(
        existingPlansGridColumns,
        "name",
        undefined,
        undefined,
        plansGridOptionalColumns
    );

    if (userRole !== "admin") {
        return (
            <div className="user-error">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
            </div>
        );
    }

    const renderPlanDetails = (showCloseIcon = true) => (
        <div className="plan-details-container">
            <div className="plan-details-header-container" style={{ padding: "0" }}>
                {showCloseIcon && (
                    <IconButton onClick={handleClosePlanDetails} size="small">
                        <CloseIcon />
                    </IconButton>
                )}
            </div>
            <div className="plan-details">
                <p className="plan-details-heading"> PLAN DETAILS</p>
                <div className="plan-content">
                    {[
                        { label: "Name", value: selectedPlan?.name },
                        { label: "Description", value: selectedPlan?.description },
                        { label: "Monthly Credits", value: selectedPlan?.monthly_credits },
                        { label: "Monthly Price", value: `$${selectedPlan?.monthly_price}` },
                        { label: "Annual Price", value: `$${selectedPlan?.annual_price}` },
                        { label: "Overage Price", value: `$${selectedPlan?.credit_overage_price}` },
                        { label: "Created", value: selectedPlan?.created },
                    ].map((field, idx) => (
                        <div className="plan-fields" key={idx}>
                            <p className="plan-field-label">{field.label}:</p>
                            <p className="plan-field-value">{field.value}</p>
                        </div>
                    ))}
                    <div className="plan-fields">
                        <p className="plan-field-label">Credit Costs:</p>
                        <div style={{ textAlign: "left" }} className="plan-field-value">
                            {selectedPlan?.credit_costs && (
                                <div style={{ whiteSpace: "pre-line" }}>
                                    {formatGridValue(selectedPlan.credit_costs)}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="plan-fields">
                        <p className="plan-field-label">Quotas:</p>
                        <div style={{ textAlign: "left" }} className="plan-field-value">
                            {selectedPlan?.quotas && (
                                <div style={{ whiteSpace: "pre-line" }}>
                                    {formatGridValue(selectedPlan.quotas)}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div>
            <PageHelmet
                title="Subscription Plans"
                description="Create and view plans"
                url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/subscription-plans`}
            />

            <div className={`plans-page ${selectedPlan ? "has-selected-plan" : ""}`}>
                <div className="plans-left-section">
                    <h2 className="existing-plans-title">Subscription Plans</h2>
                    <div className="existing-plans-container">
                        <CustomDataGrid
                            rows={existingPlansRows}
                            columns={existingPlansColumns}
                            totalRows={existingPlansRows.length}
                            onRowClick={handleRowClick}
                            selectedRowId={selectedRowId}
                            getRowHeight={() => "auto"}
                            toolbarProps={{
                                createAction: {
                                    show: true,
                                    onClick: handleOpenCreateModal,
                                    label: "+ Create"
                                },
                            }}
                        />
                        {planError && (
                            <Alert variant="danger" className="mt-3 text-center">
                                {planError}
                            </Alert>
                        )}
                    </div>
                </div>

                <div className={`plans-right-section ${isMobile && selectedPlan ? 'mobile-open' : ''}`}>
                    {!isMobile && selectedPlan && (renderPlanDetails(true))}
                    {isMobile && selectedPlan && (
                        <div className="plan-mobile-screen mobile-open">
                            <div className="mobile-header">
                                <IconButton size="small" style={{ color: '#fff' }} onClick={handleClosePlanDetails}>
                                    <MdArrowBackIos />
                                </IconButton>
                                <div className="mobile-title">{selectedPlan?.name || "Plan Details"}</div>
                            </div>

                            <div style={{ margin: "10px" }}>
                                {renderPlanDetails(false)}
                            </div>

                        </div>

                    )}
                </div>
            </div>

            {/* Create plan modal*/}
            <Modal
                show={showCreateModal}
                onHide={handleCloseCreateModal}
                centered
                backdrop="static"
                size="lg"
                dialogClassName="edit-modal-dialog"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="modal-heading">Create Plan</Modal.Title>
                </Modal.Header>
                <div className="divider"></div>

                <Modal.Body className="create-plan-card">

                    <Form onSubmit={handleCreatePlan}>

                        {/* Name and description for plan*/}
                        <div className="input-row">
                            <div className="input-box blue-border">
                                <div className="label-container">
                                    <label>
                                        Plan Name <span className="required">*</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="plan-name-tooltip">Plan name should be unique</Tooltip>}
                                        container={document.body}
                                    >
                                        <span className="d-inline-block">
                                            <IconButton>
                                                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </OverlayTrigger>

                                </div>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={planFields.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter plan name"
                                    required
                                    pattern="[A-Za-z\s]+"
                                    title="Plan name should contain only alphabets"
                                />
                            </div>
                            <div className="input-box blue-border">
                                <div className="label-container">
                                    <label>
                                        Description <span className="required">*</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="plan-description-tooltip">Plan description</Tooltip>}
                                        container={document.body}
                                    >
                                        <span className="d-inline-block">
                                            <IconButton>
                                                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </OverlayTrigger>
                                </div>
                                <Form.Control
                                    type="text"
                                    name="description"
                                    value={planFields.description}
                                    onChange={handleInputChange}
                                    placeholder="Enter plan description"
                                    required
                                    pattern="[A-Za-z\s]+"
                                    title="Plan description should contain only alphabets"
                                />
                            </div>
                        </div>

                        {/* Monthly price and annual price for plan */}
                        <div className="input-row">
                            <div className="input-box purple-border">
                                <div className="label-container">
                                    <label>
                                        Monthly Price <span className="required">*</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="monthly-price-tooltip">Enter monthly price for the plan</Tooltip>}
                                        container={document.body}
                                    >
                                        <span className="d-inline-block">
                                            <IconButton>
                                                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </OverlayTrigger>

                                </div>
                                <Form.Control
                                    type="number"
                                    name="monthly_price"
                                    value={planFields.monthly_price}
                                    onChange={handleInputChange}
                                    placeholder="Enter Monthly price for the plan"
                                    required
                                />
                            </div>
                            <div className="input-box purple-border">
                                <div className="label-container">
                                    <label>
                                        Annual Price <span className="required">*</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="annual-price-tooltip">Enter Annual price for the plan</Tooltip>}
                                        container={document.body}
                                    >
                                        <span className="d-inline-block">
                                            <IconButton>
                                                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </OverlayTrigger>

                                </div>
                                <Form.Control
                                    type="number"
                                    name="annual_price"
                                    value={planFields.annual_price}
                                    onChange={handleInputChange}
                                    placeholder="Enter Annual price for the plan"
                                    required
                                />
                            </div>
                        </div>

                        {/* Monthly credits and overage charges for features fields*/}
                        <div className="input-row">
                            <div className="input-box blue-border">
                                <div className="label-container">
                                    <label>
                                        Monthly Credits <span className="required">*</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="monthly-credits-tooltip">Monthly credits for the plan should be greater than 0</Tooltip>}
                                        container={document.body}
                                    >
                                        <span className="d-inline-block">
                                            <IconButton>
                                                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </OverlayTrigger>

                                </div>
                                <Form.Control
                                    type="number"
                                    name="monthly_credits"
                                    value={planFields.monthly_credits}
                                    onChange={handleInputChange}
                                    placeholder="Enter monthly credits"
                                    required
                                />
                            </div>

                            <div className="input-box blue-border">
                                <div className="label-container">
                                    <label>
                                        Overage Charge per Credit <span className="required">*</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="overage-charge-tooltip">Enter overage charges.</Tooltip>}
                                        container={document.body}
                                    >
                                        <span className="d-inline-block">
                                            <IconButton>
                                                <HelpOutlineIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </span>
                                    </OverlayTrigger>
                                </div>
                                <Form.Control
                                    type="number"
                                    name="credit_overage_price"
                                    value={planFields.credit_overage_price}
                                    onChange={handleInputChange}
                                    placeholder="Enter overage charge"
                                    required
                                />
                            </div>
                        </div>

                        {/* credit costs for features fields*/}
                        <div className="features-box green-border">
                            <h3>Credit Costs</h3>
                            <div className="features-grid">
                                {creditFields.map((feature) => (
                                    <div key={feature.name} className="feature-item">
                                        <div className="feature-label">
                                            {feature.label}
                                        </div>
                                        <Form.Control
                                            type="number"
                                            name={feature.name}
                                            value={planFields.credit_costs[feature.name] ?? ""}
                                            placeholder="0"
                                            onChange={handleInputChange}
                                            className="feature-input"
                                            required
                                            step="any"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* quota fields*/}

                        <div className="features-box green-border">
                            <h3>Quotas</h3>
                            <div className="features-grid">
                                {quotaFields.map((feature) => (
                                    <div key={feature.name} className="feature-item">
                                        <div className="feature-label">
                                            {feature.icon}
                                            {feature.label}
                                        </div>
                                        <Form.Control
                                            type="number"
                                            name={feature.name}
                                            value={planFields.quotas[feature.name] ?? ""}
                                            placeholder="0"
                                            onChange={handleInputChange}
                                            className="feature-input"
                                            required
                                        />
                                    </div>
                                ))}
                            </div>

                        </div>

                        {createPlanInfo && (
                            <Alert variant="success" className="mt-3 text-center">
                                {createPlanInfo}
                            </Alert>
                        )}
                        {createPlanError && (
                            <Alert variant="danger" className="mt-3 text-center">
                                {createPlanError}
                            </Alert>
                        )}

                        <div className="button-container">
                            <Button type="submit" className="create-btn">
                                Create Plan
                            </Button>
                        </div>

                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default Plans;
